'use client';

import React, { useEffect } from 'react';
import {
  defaultLanguage,
  getLanguageFromPath,
  getLanguageFromValue,
  stripLanguagePrefix,
  withLanguagePrefix,
  type SiteLanguage,
} from '@/data/languages';
import { translateText } from '@/data/siteTranslations';

const shouldSkip = (node: Node) => {
  const parent = node.parentElement;
  if (!parent) return true;
  return ['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE'].includes(parent.tagName);
};

const translateDocument = (language: SiteLanguage) => {
  if (language === 'en') return;

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    if (!shouldSkip(current)) {
      textNodes.push(current as Text);
    }
    current = walker.nextNode();
  }

  textNodes.forEach((node) => {
    const original = node.nodeValue ?? '';
    const translated = translateText(original, language);
    if (translated !== original) {
      node.nodeValue = original.replace(original.trim(), translated);
    }
  });

  document.querySelectorAll<HTMLElement>('[placeholder],[aria-label],[title]').forEach((element) => {
    ['placeholder', 'aria-label', 'title'].forEach((attr) => {
      const value = element.getAttribute(attr);
      if (!value) return;
      const translated = translateText(value, language);
      if (translated !== value) element.setAttribute(attr, translated);
    });
  });
};

const getCurrentLanguage = () => {
  const params = new URLSearchParams(window.location.search);
  return getLanguageFromPath(window.location.pathname)
    ?? getLanguageFromValue(params.get('lang') || localStorage.getItem('wto_lang') || defaultLanguage);
};

const preserveLanguageLinks = (language: SiteLanguage) => {
  const canonicalPath = stripLanguagePrefix(window.location.pathname);
  document.documentElement.lang = language;

  if (language !== defaultLanguage && window.location.pathname !== withLanguagePrefix(canonicalPath, language)) {
    return;
  }

  document.querySelectorAll<HTMLAnchorElement>('a[href^="/"]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || href.startsWith('//') || href.startsWith('/assets') || href.includes('.php')) return;
    const [pathWithQuery, hash = ''] = href.split('#');
    const [path, query = ''] = pathWithQuery.split('?');
    const nextPath = withLanguagePrefix(path || '/', language);
    const nextHref = `${nextPath}${query ? `?${query}` : ''}${hash ? `#${hash}` : ''}`;
    if (href !== nextHref) link.setAttribute('href', nextHref);
  });
};

const TranslationProvider: React.FC = () => {
  useEffect(() => {
    const language = getCurrentLanguage();
    translateDocument(language);
    preserveLanguageLinks(language);

    const observer = new MutationObserver(() => {
      translateDocument(language);
      preserveLanguageLinks(language);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return null;
};

export default TranslationProvider;
